package com.rainbowclubs.rainbow.di

import com.rainbowclubs.rainbow.data.cache.AuthCache
import com.rainbowclubs.rainbow.data.cache.BearerTokenCache
import com.rainbowclubs.rainbow.data.cache.ClubCache
import com.rainbowclubs.rainbow.data.models.UserRole
import com.rainbowclubs.rainbow.data.repositories.*
import com.rainbowclubs.rainbow.domain.repositories.*
import com.rainbowclubs.rainbow.domain.use_cases.club.CacheSelectedClubUseCase
import com.rainbowclubs.rainbow.domain.use_cases.club.GetClubUseCase
import com.rainbowclubs.rainbow.domain.use_cases.club.GetSelectedClubIdUseCase
import com.rainbowclubs.rainbow.domain.use_cases.club.UpdateClubUseCase
import com.rainbowclubs.rainbow.domain.use_cases.events.*
import com.rainbowclubs.rainbow.domain.use_cases.home.GetHomeDataUseCase
import com.rainbowclubs.rainbow.domain.use_cases.login.*
import com.rainbowclubs.rainbow.domain.use_cases.membership.CreateMembershipUseCase
import com.rainbowclubs.rainbow.domain.use_cases.membership.GetExistingMembershipsUseCase
import com.rainbowclubs.rainbow.domain.use_cases.membership.GetMembershipByIdUseCase
import com.rainbowclubs.rainbow.domain.use_cases.membership.GetPendingMembershipsUseCase
import com.rainbowclubs.rainbow.domain.use_cases.membership.UpdateMembershipUseCase
import com.rainbowclubs.rainbow.domain.use_cases.membership.UpdateMembershipStatusUseCase
import com.rainbowclubs.rainbow.domain.use_cases.user.GetUserUseCase
import com.rainbowclubs.rainbow.presentation.admin.*
import com.rainbowclubs.rainbow.presentation.club.ClubViewModel
import com.rainbowclubs.rainbow.presentation.events.EventDetailsViewModel
import com.rainbowclubs.rainbow.presentation.home.HomeViewModel
import com.rainbowclubs.rainbow.presentation.login.LoginViewModel
import com.rainbowclubs.rainbow.presentation.login.SignupViewModel
import com.rainbowclubs.rainbow.presentation.membership.MembershipDataViewModel
import com.rainbowclubs.rainbow.presentation.messages.MessagesViewModel
import com.rainbowclubs.rainbow.presentation.more.MoreViewModel
import com.rainbowclubs.rainbow.presentation.select_club.SelectClubViewModel
import com.rainbowclubs.rainbow.presentation.splash.SplashViewModel
import com.rainbowclubs.rainbow.utils.Validator
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import io.ktor.client.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.auth.*
import io.ktor.client.plugins.auth.providers.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.logging.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.Json
import org.koin.core.module.dsl.viewModel
import org.koin.core.module.dsl.viewModelOf
import org.koin.dsl.module

val commonModule = module {
    single { LoginUseCase(Firebase.auth, get(), get(), get()) }
    single { IsUserLoggedInUseCase(get()) }
    single { CreateAccountUseCase(Firebase.auth, get()) }
    single { LogOutUseCase(Firebase.auth, get(), get(), get()) }
    single { SelectClubUseCase(get()) }
    single { CacheSelectedClubUseCase(get()) }
    single { IsSelectedClubCached(get()) }
    single { GetClubUseCase(get()) }
    single { UpdateClubUseCase(get()) }
    single { GetHomeDataUseCase(get()) }
    single { GetClubEventsUseCase(get()) }
    single { CreateEventUseCase(get()) }
    single { UpdateEventUseCase(get()) }
    single { DeleteEventUseCase(get()) }
    single { GetEventByIdUseCase(get()) }
    single { AddEventCommentUseCase(get()) }
    single { UpdateEventAttendeeUseCase(get()) }
    single { SignEventAttendeeUseCase(get()) }
    single { GetSelectedClubIdUseCase(get()) }
    single { GetUserUseCase(get()) }
    single { CreateMembershipUseCase(get()) }
    single { GetExistingMembershipsUseCase(get()) }
    single { GetMembershipByIdUseCase(get()) }
    single { GetPendingMembershipsUseCase(get()) }
    single { UpdateMembershipUseCase(get()) }
    single { UpdateMembershipStatusUseCase(get()) }
    single { Validator() }
    single<HttpClient> {
        val authCache = get<AuthCache>()
        HttpClient {
            install(ContentNegotiation) {
                json(Json {
                    encodeDefaults = true
                    isLenient = true
                    coerceInputValues = true
                    ignoreUnknownKeys = true
                })
            }
            install(Logging) {
                logger = object : Logger {
                    override fun log(message: String) {
                        println("RainbowClubsKtor: $message")
                    }
                }
                level = LogLevel.ALL
            }
            install(Auth) {
                bearer {
                    loadTokens {
                        var user = Firebase.auth.currentUser
                        if (user == null && !authCache.hasSession()) {
                            return@loadTokens null
                        }

                        var attempts = 0
                        while (user == null && attempts < 50) {
                            kotlinx.coroutines.delay(100)
                            user = Firebase.auth.currentUser
                            attempts++
                        }

                        val accessToken = user?.getIdToken(false)
                        if (accessToken.isNullOrBlank()) {
                            null
                        } else {
                            BearerTokens(
                                accessToken,
                                user.getIdToken(true) ?: accessToken,
                            )
                        }
                    }
                }
            }
            defaultRequest {
                url {
                    protocol = URLProtocol.HTTPS
//                    protocol = URLProtocol.HTTP
                }
                host = "rainbow-service-450027198707.us-central1.run.app" // Change when test on Server
//                host = "192.168.0.133" // Change when test on Server
//                port = 8080
            }
        }
    }
    single<UserRepository> { UserRepositoryImpl(get(), get(), get()) }
    single<HomeRepository> { HomeRepositoryImpl(get()) }
    single<EventsRepository> { EventsRepositoryImpl(get()) }
    single<SelectClubRepository> { SelectClubRepositoryImpl(get()) }
    single<ClubRepository> { ClubRepositoryImpl(get()) }
    single<MembershipRepository> { MembershipRepositoryImpl(get()) }
    single { AuthCache(get()) }
    single { BearerTokenCache(get()) }
    single { ClubCache(get()) }

    viewModelOf(::LoginViewModel)
    viewModelOf(::SplashViewModel)
    viewModel { SignupViewModel(get(), get()) }
    viewModel { (clubId: String) -> HomeViewModel(clubId, get(), get()) }
    viewModel { (clubId: String) -> ClubViewModel(clubId, get(), get()) }
    viewModel { (clubId: String) -> MessagesViewModel(clubId, get()) }
    viewModel { (clubId: String) -> AdminViewModel(clubId, get()) }
    viewModel { (clubId: String, role: UserRole) -> ManageMembersViewModel(clubId, role) }
    viewModel { (clubId: String) -> ExistingMembersViewModel(clubId, get()) }
    viewModel { (clubId: String, membershipId: String) ->
        ExistingMemberDetailsViewModel(clubId, membershipId, get(), get(), get())
    }
    viewModel { (clubId: String) -> PendingInvitationsViewModel(clubId, get(), get()) }
    viewModel { (clubId: String, eventId: String?) -> TakeAttendanceViewModel(clubId, eventId, get(), get()) }
    viewModel { (clubId: String) -> ManageClubViewModel(clubId, get(), get()) }
    viewModel { (clubId: String) -> CreateEventViewModel(clubId, get()) }
    viewModel { (clubId: String) -> EditEventViewModel(clubId, get()) }
    viewModel { (clubId: String, eventId: String) ->
        UpdateEventViewModel(
            clubId,
            eventId,
            get(),
            get(),
            get()
        )
    }
    viewModel { (eventId: String) ->
        EventDetailsViewModel(
            eventId,
            get(),
            get(),
            get(),
            get(),
        )
    }
    viewModel { (clubId: String) -> MoreViewModel(clubId, get(), get()) }
    viewModelOf(::SelectClubViewModel)
    viewModel { (clubId: String) -> MembershipDataViewModel(clubId, get(), get(), get(), get()) }
}
